/**
 * completeness.ts — the client mirror of the backend's LOCKED completeness
 * formula (PIM Wave P2, Task 7).
 *
 * Reproduces `catalog_schema/service.py::compute_completeness` exactly, so the
 * live estimate the seller sees equals the score the server persists
 * (`products.completeness_score`, which is NOT exposed on any product-response
 * DTO — the live estimate stands in; see the P2 build log). Every input the
 * formula needs is already in the form-schema (requirement + visibility) + the
 * current form values.
 *
 * Formula (0-100): over the primary chain's resolved, policy-filtered, VISIBLE
 * bindings — R/F_r = required count/filled; C/F_c = recommended count/filled;
 * "filled" = a value would exist (content irrelevant). Hidden (visibility-
 * unmatched) bindings are excluded from BOTH numerator and denominator.
 *   - if R>0 and F_r<R  → round(50·F_r/R)
 *   - elif C==0         → 100
 *   - else              → 50 + round(50·F_c/C)
 * `round` is Python's banker's rounding (half-to-even) — matched below.
 */
import {
  flattenFields,
  isFieldFilled,
  type AttributeFormValues,
  type FormField,
  type FormSchemaResponse,
} from "@/features/seller-dashboard/attributes/formSchema";
import {
  buildControllerTypeMap,
  isFieldVisible,
} from "@/features/seller-dashboard/attributes/visibility";

/** Round-half-to-even, matching Python's built-in `round` on the same floats. */
export function bankersRound(x: number): number {
  const floor = Math.floor(x);
  const diff = x - floor;
  if (diff < 0.5) return floor;
  if (diff > 0.5) return floor + 1;
  return floor % 2 === 0 ? floor : floor + 1; // exactly .5 → nearest even
}

interface Tally {
  requiredCount: number;
  requiredFilled: number;
  recommendedCount: number;
  recommendedFilled: number;
}

function tally(schema: FormSchemaResponse, values: AttributeFormValues): Tally {
  const controllerTypeBySlug = buildControllerTypeMap(schema);
  const t: Tally = {
    requiredCount: 0,
    requiredFilled: 0,
    recommendedCount: 0,
    recommendedFilled: 0,
  };
  for (const field of flattenFields(schema)) {
    if (field.requirement !== "required" && field.requirement !== "recommended") continue;
    if (!isFieldVisible(field, controllerTypeBySlug, values)) continue;
    const filled = isFieldFilled(field, values);
    if (field.requirement === "required") {
      t.requiredCount += 1;
      if (filled) t.requiredFilled += 1;
    } else {
      t.recommendedCount += 1;
      if (filled) t.recommendedFilled += 1;
    }
  }
  return t;
}

/** The locked 0-100 completeness score for the current values. */
export function computeCompleteness(
  schema: FormSchemaResponse,
  values: AttributeFormValues,
): number {
  const { requiredCount, requiredFilled, recommendedCount, recommendedFilled } = tally(
    schema,
    values,
  );
  if (requiredCount > 0 && requiredFilled < requiredCount) {
    return bankersRound((50 * requiredFilled) / requiredCount);
  }
  if (recommendedCount === 0) return 100;
  return 50 + bankersRound((50 * recommendedFilled) / recommendedCount);
}

/**
 * Visible REQUIRED fields with no value — the client mirror of the submit
 * gate's `pim.required_missing` set. Drives the submit disabled-with-reason
 * affordance + the completeness meter's jump-links. The server 422 stays the
 * authority; this only prevents an obviously-doomed submit and guides the eye.
 */
export function requiredMissingFields(
  schema: FormSchemaResponse,
  values: AttributeFormValues,
): FormField[] {
  const controllerTypeBySlug = buildControllerTypeMap(schema);
  const out: FormField[] = [];
  for (const field of flattenFields(schema)) {
    if (field.requirement !== "required") continue;
    if (!isFieldVisible(field, controllerTypeBySlug, values)) continue;
    if (!isFieldFilled(field, values)) out.push(field);
  }
  return out;
}
